import type { EffectRegion } from "@client/types/vision";
import { getRegionBounds, isInRegion } from "./regionMask";

/**
 * Apply Gaussian blur to image data
 * Optimized for iPad performance with radius limiting
 * @param imageData - ImageData to modify
 * @param radius - Blur radius in pixels
 * @returns New blurred ImageData
 */
export function applyGaussianBlurToImageData(
	imageData: ImageData,
	radius: number,
	region?: EffectRegion | null,
): ImageData {
	if (radius <= 0) return imageData;

	// For iPad performance, limit blur radius to prevent lag
	// Allow higher blur for realistic visual acuity simulation
	// ORIGINAL VALUE: 20
	const maxRadius = 40;
	const adjustedRadius = Math.min(radius, maxRadius);

	const width = imageData.width;
	const height = imageData.height;
	const data = imageData.data;

	if (region) {
		// Region-specific blur: only process pixels inside the region (rect or polygon)
		const bounds = getRegionBounds(region);
		const rx1 = Math.max(0, Math.floor(bounds.rx1));
		const ry1 = Math.max(0, Math.floor(bounds.ry1));
		const rx2 = Math.min(width, Math.ceil(bounds.rx2));
		const ry2 = Math.min(height, Math.ceil(bounds.ry2));
		const isPolygon = region.type === "polygon";

		// Copy all pixels first (preserves non-region pixels)
		const regionOutput = new ImageData(width, height);
		regionOutput.data.set(data);
		const regionOutData = regionOutput.data;

		// Create the same Gaussian kernel as the full-image path
		const kernelSize = adjustedRadius * 2 + 1;
		const kernel = new Array(kernelSize);
		const sigma = adjustedRadius / 3;
		let sum = 0;
		for (let i = 0; i < kernelSize; i++) {
			const x = i - adjustedRadius;
			kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
			sum += kernel[i];
		}
		for (let i = 0; i < kernelSize; i++) {
			kernel[i] /= sum;
		}

		// Horizontal pass (region rows, region columns; polygon mask gates pixels)
		const tempData = new Uint8ClampedArray(data.length);
		tempData.set(data);
		for (let y = ry1; y < ry2; y++) {
			for (let x = rx1; x < rx2; x++) {
				if (isPolygon && !isInRegion(x, y, region)) continue;
				let r = 0;
				let g = 0;
				let b = 0;
				let a = 0;
				let weightSum = 0;
				for (let i = 0; i < kernelSize; i++) {
					const srcX = Math.max(0, Math.min(width - 1, x + i - adjustedRadius));
					const srcIdx = (y * width + srcX) * 4;
					const weight = kernel[i];
					r += (data[srcIdx] ?? 0) * weight;
					g += (data[srcIdx + 1] ?? 0) * weight;
					b += (data[srcIdx + 2] ?? 0) * weight;
					a += (data[srcIdx + 3] ?? 255) * weight;
					weightSum += weight;
				}
				const dstIdx = (y * width + x) * 4;
				tempData[dstIdx] = r / weightSum;
				tempData[dstIdx + 1] = g / weightSum;
				tempData[dstIdx + 2] = b / weightSum;
				tempData[dstIdx + 3] = a / weightSum;
			}
		}

		// Vertical pass (region rows, region columns; polygon mask gates pixels)
		for (let y = ry1; y < ry2; y++) {
			for (let x = rx1; x < rx2; x++) {
				if (isPolygon && !isInRegion(x, y, region)) continue;
				let r = 0;
				let g = 0;
				let b = 0;
				let a = 0;
				let weightSum = 0;
				for (let i = 0; i < kernelSize; i++) {
					const srcY = Math.max(
						0,
						Math.min(height - 1, y + i - adjustedRadius),
					);
					const srcIdx = (srcY * width + x) * 4;
					const weight = kernel[i];
					r += (tempData[srcIdx] ?? 0) * weight;
					g += (tempData[srcIdx + 1] ?? 0) * weight;
					b += (tempData[srcIdx + 2] ?? 0) * weight;
					a += (tempData[srcIdx + 3] ?? 255) * weight;
					weightSum += weight;
				}
				const dstIdx = (y * width + x) * 4;
				regionOutData[dstIdx] = r / weightSum;
				regionOutData[dstIdx + 1] = g / weightSum;
				regionOutData[dstIdx + 2] = b / weightSum;
				regionOutData[dstIdx + 3] = a / weightSum;
			}
		}

		return regionOutput;
	}

	// Full-image blur path (original behavior)
	const output = new ImageData(width, height);
	const outData = output.data;

	// Create Gaussian kernel
	const kernelSize = adjustedRadius * 2 + 1;
	const kernel = new Array(kernelSize);
	const sigma = adjustedRadius / 3;
	let sum = 0;

	for (let i = 0; i < kernelSize; i++) {
		const x = i - adjustedRadius;
		kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
		sum += kernel[i];
	}

	// Normalize kernel
	for (let i = 0; i < kernelSize; i++) {
		kernel[i] /= sum;
	}

	// Apply horizontal blur
	const tempData = new Uint8ClampedArray(data.length);
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			let r = 0;
			let g = 0;
			let b = 0;
			let a = 0;
			let weightSum = 0;

			for (let i = 0; i < kernelSize; i++) {
				const srcX = Math.max(0, Math.min(width - 1, x + i - adjustedRadius));
				const srcIdx = (y * width + srcX) * 4;
				const weight = kernel[i];

				r += (data[srcIdx] ?? 0) * weight;
				g += (data[srcIdx + 1] ?? 0) * weight;
				b += (data[srcIdx + 2] ?? 0) * weight;
				a += (data[srcIdx + 3] ?? 255) * weight;
				weightSum += weight;
			}

			const dstIdx = (y * width + x) * 4;
			tempData[dstIdx] = r / weightSum;
			tempData[dstIdx + 1] = g / weightSum;
			tempData[dstIdx + 2] = b / weightSum;
			tempData[dstIdx + 3] = a / weightSum;
		}
	}

	// Apply vertical blur
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			let r = 0;
			let g = 0;
			let b = 0;
			let a = 0;
			let weightSum = 0;

			for (let i = 0; i < kernelSize; i++) {
				const srcY = Math.max(0, Math.min(height - 1, y + i - adjustedRadius));
				const srcIdx = (srcY * width + x) * 4;
				const weight = kernel[i];

				r += (tempData[srcIdx] ?? 0) * weight;
				g += (tempData[srcIdx + 1] ?? 0) * weight;
				b += (tempData[srcIdx + 2] ?? 0) * weight;
				a += (tempData[srcIdx + 3] ?? 255) * weight;
				weightSum += weight;
			}

			const dstIdx = (y * width + x) * 4;
			outData[dstIdx] = r / weightSum;
			outData[dstIdx + 1] = g / weightSum;
			outData[dstIdx + 2] = b / weightSum;
			outData[dstIdx + 3] = a / weightSum;
		}
	}

	return output;
}

/**
 * Extreme blur via downscale → Gaussian → upscale.
 * Downscaling destroys high-frequency detail, and the upscale interpolation
 * adds further smoothing. Much stronger than large-kernel Gaussian alone,
 * and dramatically faster (blurs a tiny image instead of the full one).
 *
 * @param imageData - Source image data
 * @param scaleFactor - Downscale factor (e.g., 4 = quarter size, 8 = eighth)
 * @param blurRadius - Gaussian radius to apply on the small image
 * @param region - Optional region to restrict the effect
 */
export function applyExtremeBlur(
	imageData: ImageData,
	scaleFactor: number,
	blurRadius: number,
	region?: EffectRegion | null,
): ImageData {
	const w = imageData.width;
	const h = imageData.height;
	const src = imageData.data;

	// Determine processing bounds (full image or region bounding box)
	const bounds = region
		? getRegionBounds(region)
		: { rx1: 0, ry1: 0, rx2: w, ry2: h };
	const rx1 = Math.max(0, Math.floor(bounds.rx1));
	const ry1 = Math.max(0, Math.floor(bounds.ry1));
	const rx2 = Math.min(w, Math.ceil(bounds.rx2));
	const ry2 = Math.min(h, Math.ceil(bounds.ry2));
	const rw = rx2 - rx1;
	const rh = ry2 - ry1;
	const isPolygon = region?.type === "polygon";

	if (rw <= 0 || rh <= 0) return imageData;

	// Small image dimensions
	const sw = Math.max(1, Math.ceil(rw / scaleFactor));
	const sh = Math.max(1, Math.ceil(rh / scaleFactor));

	// Step 1: Downscale with box filter (area averaging)
	const small = new ImageData(sw, sh);
	const sd = small.data;
	for (let sy = 0; sy < sh; sy++) {
		for (let sx = 0; sx < sw; sx++) {
			let r = 0;
			let g = 0;
			let b = 0;
			let a = 0;
			let count = 0;
			const srcYStart = ry1 + sy * scaleFactor;
			const srcYEnd = Math.min(ry2, srcYStart + scaleFactor);
			const srcXStart = rx1 + sx * scaleFactor;
			const srcXEnd = Math.min(rx2, srcXStart + scaleFactor);
			for (let oy = srcYStart; oy < srcYEnd; oy++) {
				for (let ox = srcXStart; ox < srcXEnd; ox++) {
					const idx = (oy * w + ox) * 4;
					r += src[idx] ?? 0;
					g += src[idx + 1] ?? 0;
					b += src[idx + 2] ?? 0;
					a += src[idx + 3] ?? 255;
					count++;
				}
			}
			const sidx = (sy * sw + sx) * 4;
			sd[sidx] = r / count;
			sd[sidx + 1] = g / count;
			sd[sidx + 2] = b / count;
			sd[sidx + 3] = a / count;
		}
	}

	// Step 2: Apply Gaussian blur on the small image (no region — blur it all)
	const blurred = applyGaussianBlurToImageData(small, blurRadius);
	const bd = blurred.data;

	// Step 3: Upscale back with bilinear interpolation and composite into output
	const output = new ImageData(w, h);
	output.data.set(src); // Copy original pixels first

	const od = output.data;
	for (let y = ry1; y < ry2; y++) {
		for (let x = rx1; x < rx2; x++) {
			if (isPolygon && region && !isInRegion(x, y, region)) continue;
			// Map back to small image coordinates
			const fx = ((x - rx1) / rw) * (sw - 1);
			const fy = ((y - ry1) / rh) * (sh - 1);
			const x0 = Math.floor(fx);
			const x1b = Math.min(sw - 1, x0 + 1);
			const y0 = Math.floor(fy);
			const y1b = Math.min(sh - 1, y0 + 1);
			const dx = fx - x0;
			const dy = fy - y0;

			// Bilinear sample from blurred small image
			const i00 = (y0 * sw + x0) * 4;
			const i10 = (y0 * sw + x1b) * 4;
			const i01 = (y1b * sw + x0) * 4;
			const i11 = (y1b * sw + x1b) * 4;

			const oidx = (y * w + x) * 4;
			for (let c = 0; c < 4; c++) {
				const top = (bd[i00 + c] ?? 0) * (1 - dx) + (bd[i10 + c] ?? 0) * dx;
				const bot = (bd[i01 + c] ?? 0) * (1 - dx) + (bd[i11 + c] ?? 0) * dx;
				od[oidx + c] = top * (1 - dy) + bot * dy;
			}
		}
	}

	return output;
}
